import { useLocalization } from '@experiences/locales';
import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { formatDate } from '@experiences/util';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { FontVariantToken } from '@uipath/apollo-core';
import React, { useCallback } from 'react';
import type {
    ControllerRenderProps,
    FieldError,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
    ApiFlavor,
    ByoFormErrorTypes,
    type ByoLlmConfiguration,
    type ByoOperationGroupConfiguration,
    IntegrationServiceConnectorTypesToLlmConnectorTypes,
    type LlmConnectorType,
} from '../../../../services/llm-gateway/LlmGateway.types';
import {
    ApiFlavorsToLlmConnectorTypes,
    createConfigurationWithAlternative,
    getConnectorDropdownValue,
    getCurrentSelectedAlternative,
    getDefaultAvailableApiFlavors,
    getDeprecatedModelsWithInfo,
    getModelConfiguration,
    getModelDescriptionTranslationKey,
    getModelIndex,
    getModelTitleTranslationKey,
    handleConnectorChange,
    translationKeyExists,
} from '../../../../services/llm-gateway/LlmGateway.utils';
import type { AddEditConfigurationFormData } from '../AddEditConfigurationViewModel';
import { AvailableModelsAutocomplete } from './AvailableModelsAutocomplete';
import { ConnectionDropdown } from './ConnectionsDropdown';
import { ConnectorDropdown } from './ConnectorDropdown';
import { LlmApiFlavorDropdown } from './LlmApiFlavorDropdown';
import { ModelAlternativeDropdown } from './ModelAlternativeDropdown';

const useStyles = makeStyles(theme =>
    createStyles({
        modelCard: {
            border: `1px solid ${theme.palette.semantic.colorBorderDeEmp}`,
            borderRadius: '8px',
            padding: theme.spacing(2),
            backgroundColor: theme.palette.semantic.colorBackground,
            width: 'fit-content',
        },
        warningText: {
            color: theme.palette.error.main,
            marginTop: theme.spacing(0.5),
            fontSize: '12px',
        },
    }),
);

type AllModelsConfigurationSectionProps = {
    organizationId: string;
    tenantId: string;
    folderKey?: string;
    llmConfigurationsField: ControllerRenderProps<AddEditConfigurationFormData, 'llmConfigurations'>;
    notifyFieldChanges: () => void;
    llmToConnectorTypeMapping: Record<string, LlmConnectorType[]>;
    vendorLlmApiFlavors?: Record<string, ApiFlavor[] | null>;
    product: string;
    feature: string;
    productConfigurations?: Record<string, Record<string, ByoOperationGroupConfiguration>>;
    error?: FieldError;
};

export const AllModelsConfigurationSection: React.FC<AllModelsConfigurationSectionProps> = ({
    organizationId,
    tenantId,
    folderKey,
    llmConfigurationsField,
    notifyFieldChanges,
    llmToConnectorTypeMapping,
    vendorLlmApiFlavors,
    product,
    feature,
    productConfigurations,
    error,
}: AllModelsConfigurationSectionProps) => {
    const { formatMessage: translate } = useIntl();
    const language = useLocalization();
    const classes = useStyles();

    const handleModelAlternativeChange = useCallback((desiredModel: string, llmConfiguration: Partial<ByoLlmConfiguration>) =>
        (selectedAlternative: string, isCustomAlias: boolean) => {
            const updatedConfiguration = createConfigurationWithAlternative(
                llmConfiguration,
                selectedAlternative,
                desiredModel,
                false,
                isCustomAlias
            );

            llmConfigurationsField.onChange({
                ...llmConfigurationsField.value,
                [desiredModel]: updatedConfiguration,
            });

            notifyFieldChanges();
        }, [ llmConfigurationsField, notifyFieldChanges ]);

    const deprecatedModelsInfo = getDeprecatedModelsWithInfo(productConfigurations, product, feature);

    const getModelOverLapError = useCallback((llmConfiguration: Partial<ByoLlmConfiguration>) => {
        // No error can apply to like-for-like replacements
        if (!llmConfiguration.isCustomAlias) {
            return undefined;
        }

        // Only handle ModelNameOverlap errors
        if (error?.type !== ByoFormErrorTypes.ModelNameOverlap) {
            return undefined;
        }

        const errorModelName = error.message?.split(',')[1];

        // Ignore error if it doesn't pertain to the current model custom alias
        if (errorModelName === llmConfiguration.desiredModel) {
            return error;
        }

        return undefined;
    }, [ error ]);

    return (<>
        {Object.entries(llmConfigurationsField.value).map(([ desiredModel, llmConfiguration ]) => {
            // Get API flavors based on the connector types or generic alternative probes for the model
            const currentModel = llmConfiguration.desiredModel ?? desiredModel;

            const modelConfig = getModelConfiguration(
                productConfigurations,
                product,
                feature,
                llmConfiguration.defaultModel ?? desiredModel
            );

            const isGenericAlternative = !Object.keys(llmToConnectorTypeMapping).includes(currentModel);

            const availableApiFlavors = getDefaultAvailableApiFlavors(vendorLlmApiFlavors, productConfigurations, product, feature, currentModel);

            const baseModelName = llmConfiguration.defaultModel ?? desiredModel;
            const modelIndex = getModelIndex(productConfigurations, product, feature, baseModelName);
            const titleKey = modelIndex >= 0 ? getModelTitleTranslationKey(product, feature, modelIndex) : undefined;
            const descriptionKey = modelIndex >= 0 ? getModelDescriptionTranslationKey(product, feature, modelIndex) : undefined;
            const showModelTitle = !!titleKey && translationKeyExists(translate, titleKey);
            const showModelDescription = !!descriptionKey && translationKeyExists(translate, descriptionKey);

            // Determine probes at model/alternative/generic alternative level (these override the default)
            const currentSelectedModel = getCurrentSelectedAlternative(llmConfiguration);
            let apiFlavorProbes;

            if (!currentSelectedModel || currentSelectedModel === desiredModel) {
                apiFlavorProbes = modelConfig?.probes;
            } else {
                // Check for specific alternative probes first
                const alternative = modelConfig?.alternativesWithProbes?.find(
                    alt => alt.model === currentSelectedModel
                );

                if (alternative?.probes) {
                    apiFlavorProbes = alternative.probes;
                } else if (isGenericAlternative) {
                    apiFlavorProbes = modelConfig?.genericAlternativeProbes;
                }
            }

            const selectedModelInfo = currentSelectedModel && deprecatedModelsInfo
                ? deprecatedModelsInfo.get(currentSelectedModel)
                : null;

            const deprecationWarning = selectedModelInfo
                ? translate({ id: 'CLIENT_BYO_LLM_TO_BE_RETIRED_ON' }, { date: formatDate(selectedModelInfo.usageEndDate, language) })
                : null;

            const apiFlavorWarnings = (() => {
                if (!llmConfiguration.apiFlavor) {
                    return [];
                }
                const probe = apiFlavorProbes?.find(p => p.apiFlavor === llmConfiguration.apiFlavor);
                return probe?.warningCodes ?? [];
            })();

            const apiFlavorWarningText = apiFlavorWarnings.length > 0
                ? apiFlavorWarnings.map(code => translate({ id: code.toUpperCase() })).join('. ')
                : null;

            return (<UiStack
                key={`llm-configuration-${desiredModel}`}
                data-cy={`llm-configuration-${desiredModel}`}
                className={classes.modelCard}
                gap={SpacingToken.M}
                direction='column'
            >
                {(showModelTitle || showModelDescription) && (
                    <UiStack
                        direction='column'
                        gap={SpacingToken.XS}>
                        {showModelTitle && titleKey && (
                            <UiText variant={FontVariantToken.fontSizeMBold}>
                                {translate({ id: titleKey })}
                            </UiText>
                        )}
                        {showModelDescription && descriptionKey && (
                            <UiText>
                                {translate({ id: descriptionKey })}
                            </UiText>
                        )}
                    </UiStack>
                )}
                <UiStack
                    wrap='wrap'
                    gap={SpacingToken.M}
                    direction='row'
                    align='start'
                >
                    <UiStack
                        direction='column'
                        gap={SpacingToken.XS}
                    >
                        <ModelAlternativeDropdown
                            targetProduct={product}
                            targetFeature={feature}
                            targetModel={llmConfiguration.defaultModel ?? desiredModel}
                            productConfigurations={productConfigurations ?? {}}
                            error={getModelOverLapError(llmConfiguration)}
                            value={getCurrentSelectedAlternative(llmConfiguration)}
                            onChange={handleModelAlternativeChange(desiredModel, llmConfiguration)}
                            onBlur={llmConfigurationsField.onBlur}
                            deprecatedModelsInfo={deprecatedModelsInfo}
                        />
                        {deprecationWarning && (
                            <UiText
                                variant={FontVariantToken.fontSizeXs}
                                className={classes.warningText}
                            >
                                {deprecationWarning}
                            </UiText>
                        )}
                    </UiStack>
                    <UiStack
                        direction='column'
                        gap={SpacingToken.XS}
                    >
                        <LlmApiFlavorDropdown
                            value={llmConfiguration.apiFlavor}
                            disabled={!!getModelOverLapError(llmConfiguration) || !desiredModel}
                            availableApiFlavors={availableApiFlavors}
                            probes={apiFlavorProbes}
                            llmConfigurationsField={llmConfigurationsField}
                            notifyFieldChanges={notifyFieldChanges}
                            desiredModel={desiredModel}
                            llmConfiguration={llmConfiguration}
                            autoSelectFirst
                        />
                        {apiFlavorWarningText && (
                            <UiText
                                variant={FontVariantToken.fontSizeXs}
                                className={classes.warningText}
                            >
                                {apiFlavorWarningText}
                            </UiText>
                        )}
                    </UiStack>
                </UiStack>
                <UiStack
                    wrap='wrap'
                    gap={SpacingToken.M}
                    direction='row'>
                    <ConnectorDropdown
                        organizationId={organizationId}
                        tenantId={tenantId}
                        label={translate({ id: 'CLIENT_CG_CONNECTOR' })}
                        required
                        disabled={!llmConfiguration.apiFlavor}
                        apiFlavor={llmConfiguration.apiFlavor}
                        restrictToCustomAiGateway={
                            llmConfiguration.apiFlavor === ApiFlavor.OpenAiChatCompletions
                            && !!llmToConnectorTypeMapping[currentModel]
                            && !llmToConnectorTypeMapping[currentModel].some(
                                (ct: LlmConnectorType) => ct === 'OpenAi' || ct === 'AzureOpenAi' || ct === 'OpenAiV1Compatible'
                            )
                        }
                        filter={connectorKey => {
                            if (connectorKey === 'uipath-aws-bedrock') {
                                return false;
                            }

                            // If dropdown is disabled, short-circuit filter to avoid issues
                            if (!llmConfiguration.apiFlavor) {
                                return false;
                            }

                            const isLlmVendorConnector = connectorKey in IntegrationServiceConnectorTypesToLlmConnectorTypes;

                            if (!isLlmVendorConnector) {
                                return false;
                            }

                            if (isGenericAlternative && llmConfiguration.apiFlavor) {
                                return ApiFlavorsToLlmConnectorTypes[llmConfiguration.apiFlavor]
                                    .includes(IntegrationServiceConnectorTypesToLlmConnectorTypes[connectorKey]);
                            }

                            return llmToConnectorTypeMapping[llmConfiguration.desiredModel ?? desiredModel]
                                .includes(IntegrationServiceConnectorTypesToLlmConnectorTypes[connectorKey]);
                        }}
                        value={getConnectorDropdownValue(llmConfiguration)}
                        onChange={(value, tags) => {
                            handleConnectorChange(value, llmConfiguration, tags);
                            llmConfigurationsField.onChange({
                                ...llmConfigurationsField.value,
                                [desiredModel]: llmConfiguration,
                            });
                            notifyFieldChanges();
                        }} />
                    <ConnectionDropdown
                        organizationId={organizationId}
                        tenantId={tenantId}
                        folderKey={folderKey}
                        connectorKey={getConnectorDropdownValue(llmConfiguration)}
                        label={translate({ id: 'CLIENT_CG_CONNECTION' })}
                        required
                        disabled={!llmConfiguration.connectorType}
                        value={llmConfiguration.connectionId}
                        onChange={(value) => {
                            llmConfiguration.connectionId = value.id;
                            llmConfiguration.customerModel = '';
                            llmConfigurationsField.onChange({
                                ...llmConfigurationsField.value,
                                [desiredModel]: llmConfiguration,
                            });

                            notifyFieldChanges();
                        }} />
                    <AvailableModelsAutocomplete
                        label={translate({ id: 'CLIENT_BYO_LLM_CONNECTED_MODEL_ID' })}
                        required
                        disabled={!llmConfiguration.connectionId}
                        organizationId={organizationId}
                        tenantId={tenantId}
                        byoLlmConfiguration={llmConfiguration}
                        initialValue={llmConfiguration.customerModel ?? ''}
                        onChange={(value) => {
                            llmConfiguration.customerModel = value ?? '';
                            llmConfigurationsField.onChange({
                                ...llmConfigurationsField.value,
                                [desiredModel]: llmConfiguration,
                            });

                            notifyFieldChanges();
                        }} />
                </UiStack>
            </UiStack>
            );
        })}
    </>);
};
